import {
  CreateButton,
  DeleteButton,
  EditButton,
  ListButton,
  RefreshButton,
  Show,
  useModal,
} from "@refinedev/antd";

import { Button, Space, Tag } from "antd";
import { IResourceComponentsProps, useNavigation, useShow } from "@refinedev/core";
import {
  TeamOutlined,
  BankOutlined,
  FileProtectOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";

import { IShipcoGroup } from "interfaces";
import React from "react";
import {
  ShowField,
  ShowSection,
  ShowFieldGrid,
  ShowModal,
  ShowModalItem,
} from "components/show";

export const ShipcoGroupShow: React.FC<IResourceComponentsProps> = () => {
  const { result, query: { isLoading } } = useShow<IShipcoGroup>();
  const record = result;

  const { list } = useNavigation();

  const {
    modalProps: modalPropsShipco,
    show: showShipco,
    close: closeShipco,
  } = useModal();
  const {
    modalProps: modalPropsLicence,
    show: showLicence,
    close: closeLicence,
  } = useModal();
  const {
    modalProps: modalPropsContacts,
    show: showContacts,
    close: closeContacts,
  } = useModal();
  const {
    modalProps: modalPropsAddress,
    show: showAddress,
    close: closeAddress,
  } = useModal();

  const hasShipco = !!record?.shipco;
  const hasAddress = !!record?.address;
  const hasLicences = Array.isArray(record?.licences) && (record?.licences?.length ?? 0) > 0;
  const hasContacts = Array.isArray(record?.contacts) && (record?.contacts?.length ?? 0) > 0;

  return (
    <Show
      breadcrumb={false}
      headerProps={{
        onBack: () => window.history.back(),
        subTitle: "active shipco group",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <Space>
            <ListButton resource="shipco-group">Shipco Group</ListButton>
            <EditButton resource="shipco-group" recordItemId={record?.id} />
            <DeleteButton
              resource="shipco-group"
              recordItemId={record?.id}
              onSuccess={() => list("subscription")}
            />
            <RefreshButton resource="shipco-group" recordItemId={record?.id} />
          </Space>
        ),
      }}
      isLoading={isLoading}
      footerButtons={
        <Space>
          {hasShipco && (
            <Button type="default" onClick={showShipco}>
              Shipco Details
            </Button>
          )}
          {hasAddress ? (
            <Button type="default" onClick={showAddress}>
              Shipco Group Address
            </Button>
          ) : (
            <CreateButton resource="address">Setup Address</CreateButton>
          )}
          {hasLicences && (
            <Button type="default" onClick={showLicence}>
              Associated Licences
            </Button>
          )}
          {hasContacts ? (
            <Button type="default" onClick={showContacts}>
              Associated Contacts
            </Button>
          ) : (
            <CreateButton resource="contact">Setup Contact</CreateButton>
          )}
        </Space>
      }
    >
      <Space direction="vertical" size={24} style={{ width: "100%" }}>
        {/* Group Registration */}
        <ShowSection
          title="Group Registration"
          subtitle="Legal business registration details"
          icon={<TeamOutlined />}
          variant="elevated"
        >
          <ShowFieldGrid columns={2}>
            <ShowField label="Registration Name" value={record?.registrationName} />
            <ShowField label="Registration Number" value={record?.registrationNumber} type="code" copyable />
            <ShowField label="GST Number" value={record?.gstNumber} type="code" copyable />
            <ShowField label="Accreditation Number" value={record?.accreditationNumber} type="code" copyable />
            <ShowField label="Phone" value={record?.phone} type="phone" />
            <ShowField label="Communication Email" value={record?.email} type="email" />
          </ShowFieldGrid>
        </ShowSection>

        {/* Parent Shipco */}
        {hasShipco && (
          <ShowSection
            title="Parent Shipping Company"
            subtitle="Associated shipco entity"
            icon={<BankOutlined />}
            variant="default"
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="Shipco Name" value={record?.shipco?.registrationName} />
            </ShowFieldGrid>
          </ShowSection>
        )}
      </Space>

      {/* Shipco Modal */}
      <ShowModal
        title="Shipco Details"
        subtitle={record?.shipco?.registrationName}
        icon={<BankOutlined />}
        open={modalPropsShipco.open ?? false}
        onClose={closeShipco}
        empty={!hasShipco}
        emptyText="No shipco associated with this group"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Registration Name" value={record?.shipco?.registrationName} />
          <ShowField label="Registration Number" value={record?.shipco?.registrationNumber} type="code" copyable />
          <ShowField label="GST Number" value={record?.shipco?.gstNumber} type="code" copyable />
          <ShowField label="Accreditation Number" value={record?.shipco?.accreditationNumber} type="code" copyable />
          <ShowField label="Phone" value={record?.shipco?.phone} type="phone" />
          <ShowField label="Communication Email" value={record?.shipco?.email} type="email" />
        </ShowFieldGrid>
      </ShowModal>

      {/* Licences Modal */}
      <ShowModal
        title="Associated Licences"
        subtitle={record?.registrationName}
        icon={<FileProtectOutlined />}
        open={modalPropsLicence.open ?? false}
        onClose={closeLicence}
        empty={!hasLicences}
        emptyText="No licences associated with this group"
        width={700}
      >
        {record?.licences?.map((licence, index) => (
          <ShowModalItem
            key={index}
            title={licence?.vessel?.name || `Licence ${index + 1}`}
            showDivider={index < (record?.licences?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="UKHO Licence" value={licence?.ukhoLicenceId} type="code" copyable />
              <ShowField label="Vessel Name" value={licence?.vessel?.name} />
              <ShowField label="Vessel Type" value={licence?.vessel?.type} type="tag" tagColor="blue" />
              <ShowField label="IMO Number" value={licence?.vessel?.imoNumber} type="code" />
              <ShowField label="Call Sign" value={licence?.vessel?.callSign} type="code" />
              <ShowField label="Status" value={licence?.vessel?.holdStatus} type="status" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Contacts Modal */}
      <ShowModal
        title="Associated Contacts"
        subtitle={record?.registrationName}
        icon={<UserOutlined />}
        open={modalPropsContacts.open ?? false}
        onClose={closeContacts}
        empty={!hasContacts}
        emptyText="No contacts associated with this group"
      >
        {record?.contacts?.map((contact, index) => (
          <ShowModalItem
            key={index}
            title={`Contact ${index + 1}`}
            showDivider={index < (record?.contacts?.length ?? 0) - 1}
          >
            <ShowFieldGrid columns={2}>
              <ShowField label="Name" value={contact?.name} />
              <ShowField label="Email" value={contact?.email} type="email" />
              <ShowField label="Phone" value={contact?.phone} type="phone" />
            </ShowFieldGrid>
          </ShowModalItem>
        ))}
      </ShowModal>

      {/* Address Modal */}
      <ShowModal
        title="Shipco Group Address"
        subtitle={record?.registrationName}
        icon={<EnvironmentOutlined />}
        open={modalPropsAddress.open ?? false}
        onClose={closeAddress}
        empty={!hasAddress}
        emptyText="No address configured for this group"
      >
        <ShowFieldGrid columns={2}>
          <ShowField label="Address Line 1" value={record?.address?.addressLine1} />
          <ShowField label="Address Line 2" value={record?.address?.addressLine2} />
          <ShowField label="Address Line 3" value={record?.address?.addressLine3} />
          <ShowField label="City" value={record?.address?.city} />
          <ShowField label="State" value={record?.address?.state} />
          <ShowField label="Country" value={record?.address?.country?.name} />
          <ShowField label="Postal Code" value={record?.address?.postalCode} type="code" />
        </ShowFieldGrid>
      </ShowModal>
    </Show>
  );
};
